import { useState } from "react";
import { C } from "./re-colors.ts";
import { TEST_DATA, LENDING_PIPELINE, INVESTMENT_PORTFOLIO, CD_MATURITY_LADDER, LIQUIDITY_METRICS, MORTGAGE_PIPELINE, WEALTH_DATA, BRANCH_DATA, WATCHLIST_CREDITS, LOAN_MODIFICATIONS, AG_CROP_CONCENTRATION, _tasks, BRIEFINGS } from "./re-data.ts";
import { fmt, KpiCard, InsightCard, TwoCol, Bdg, SDot, Card, Bar, useIsMobile } from "./re-components.tsx";

// ── DATE CONTEXT HELPER ───────────────────────────────────────────
// Reference date: BANK.asOfDate = "March 2, 2026"
const REF = new Date(2026, 2, 2);
const MONTH_IDX: Record<string, number> = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };

function relDate(dateStr: string): { label: string; color: string; isPast: boolean; isToday: boolean } {
  const parts = dateStr.trim().split(" ");
  if (parts.length < 2) return { label: "", color: C.dim, isPast: false, isToday: false };
  const target = new Date(2026, MONTH_IDX[parts[0]] ?? 0, parseInt(parts[1]));
  const diff = Math.round((target.getTime() - REF.getTime()) / 86400000);
  if (diff === 0)  return { label: "TODAY",       color: C.burgundy, isPast: false, isToday: true  };
  if (diff < 0)    return { label: `${Math.abs(diff)}d ago`, color: C.dim,      isPast: true,  isToday: false };
  if (diff === 1)  return { label: "Tomorrow",    color: C.gold,     isPast: false, isToday: false };
  if (diff <= 7)   return { label: `In ${diff}d`, color: C.gold,     isPast: false, isToday: false };
  if (diff <= 30)  return { label: `${diff} days`,color: C.muted,    isPast: false, isToday: false };
  return             { label: `${Math.round(diff/7)}w`,  color: C.dim,      isPast: false, isToday: false };
}
import {
  DepositConcentrationDetail, SBAPipelineDetail, NIMDetail, CECLDetail,
  CREDetail, NPLDetail, AtRiskRevenueDetail, PortfolioLoansDetail,
  CompetitorDetail, ProspectsDetail, ACHDetail, OFACDetail,
  VaultDetail, RegCCDetail, SARDetail, CRADetail, HMDADetail,
  BSAReviewsDetail, OaksStreetDetail, AgDroughtDetail,
  CECLScenariosDetail, LendingPipelineDetail, CustomerAtRiskDetail,
  InvestmentPortfolioDetail, CDLadderDetail, LCRDetail,
  LoanFileDetail, WatchlistCommitteeDetail, LoanModificationDetail,
  AgCropConcentrationDetail, MortgagePipelineDetail,
  WealthAUMDetail, BranchDrilldownDetail,
  DepositProfitabilityDetail, HMDALARDetail, FairLendingDetail,
  DerivativesDetail, ModelGovernanceDetail,
} from "./re-modals.tsx";
import { MorningBriefing } from "./re-panels.tsx";
import { AIInsightsPanel } from "./re-ai.tsx";

type Persona = {
  id: string; name: string; full: string; label: string;
  initials: string; icon: string; color: string; grad: string;
};

// ── PRIORITY ACTION BANNER ────────────────────────────────────────
function PriorityBanner({ pid, onEscalate, onNavTasks }: { pid: string; onEscalate: () => void; onNavTasks?: () => void }) {
  const criticalTasks = (_tasks[pid] || []).filter((t: any) => t.priority === "critical" && !t.done);
  const highTasks     = (_tasks[pid] || []).filter((t: any) => t.priority === "high"     && !t.done);
  const briefItems    = (BRIEFINGS[pid]?.items || []).filter((i: any) => i.urgency === "critical");
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || (criticalTasks.length === 0 && briefItems.length === 0)) return null;

  return (
    <div style={{ background: `${C.burgundy}08`, border: `1.5px solid ${C.burgundy}30`, borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          {/* Alert counts */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: criticalTasks.length > 0 ? 10 : 0 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.burgundy, animation: "pulse 2s infinite", flexShrink: 0 }} />
            <span style={{ fontWeight: 800, fontSize: 13, color: C.burgundy }}>
              {criticalTasks.length} Critical · {highTasks.length} High Priority
            </span>
            {onNavTasks && (
              <button onClick={onNavTasks} style={{ fontSize: 10, color: C.burgundy, background: `${C.burgundy}12`, border: `1px solid ${C.burgundy}30`, borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontWeight: 700 }}>View All Tasks</button>
            )}
            <button onClick={onEscalate} style={{ fontSize: 10, color: "#fff", background: C.burgundy, border: "none", borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontWeight: 700, marginLeft: "auto" }}>↑ Escalate</button>
          </div>
          {/* Top critical items */}
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {criticalTasks.slice(0, 2).map((t: any, i: number) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: C.burgundy, color: "#fff", fontWeight: 700, flexShrink: 0 }}>CRITICAL</span>
                <span style={{ color: C.text, fontWeight: 600 }}>{t.title}</span>
                <span style={{ color: C.muted, fontSize: 10, flexShrink: 0 }}>Due {t.due}</span>
              </div>
            ))}
            {briefItems.slice(0,1).map((item: any, i: number) => (
              <div key={"b"+i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: `${C.burgundy}20`, color: C.burgundy, fontWeight: 700, border: `1px solid ${C.burgundy}40`, flexShrink: 0 }}>ALERT</span>
                <span style={{ color: C.muted }}>{item.text.substring(0, 100)}{item.text.length > 100 ? "…" : ""}</span>
              </div>
            ))}
          </div>
        </div>
        <button onClick={() => setDismissed(true)} style={{ background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 14, padding: "0 2px", flexShrink: 0 }}>✕</button>
      </div>
    </div>
  );
}

type Escalation = { from: string; fromRole: string; to: string; msg: string; priority: string };

// ── CEO DASHBOARD ─────────────────────────────────────────────────
function CEODash({ openModal, onEscalate }: { openModal: (m: string) => void; onEscalate: () => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        <KpiCard label="Total Assets"    value="$4.21B"  sub="2.3% YTD"           trend="up" spark={[3.98,4.01,4.08,4.11,4.17,4.21]} color={C.burgundy} />
        <KpiCard label="Net Income YTD"  value="$7.9M"   sub="+6.2% vs budget"    trend="up" spark={[1.1,2.3,3.9,5.1,6.4,7.9]}       color={C.forest}   onClick={() => openModal("nim")} />
        <KpiCard label="NIM"             value="3.42%"   sub="-8bps QoQ"          trend="dn" spark={[3.72,3.61,3.55,3.50,3.47,3.42]}  color={C.burgundy} onClick={() => openModal("nim")} />
        <KpiCard label="ROAA"            value="1.14%"   sub="Peer avg 1.08%"     trend="up" spark={[1.02,1.05,1.08,1.10,1.12,1.14]}  color={C.forest}   />
        <KpiCard label="Deposits"        value="$3.62B"  sub="-0.8% MoM"          trend="dn" spark={[3.72,3.68,3.65,3.63,3.62,3.62]}  color={C.gold}     onClick={() => openModal("deposits")} />
      </div>
      <TwoCol
        left={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <InsightCard icon="🚨" title="Deposit Concentration: Top 3 = 18.4% of Total Deposits"     body="Metro Motors ($284M), Regional Ag Cooperative ($218M), Summit Holdings LLC ($166M). Any single exit triggers FDIC scrutiny."               bdg="Action Required" bColor={C.burgundy} priority="critical" onClick={() => openModal("deposits")} />
            <InsightCard icon="📈" title="SBA Pipeline: $42M — 34% Below CA Peer Median"               body="7 existing clients pre-qualified. Dedicated SBA banker could capture $420K in incremental fees this quarter."               bdg="Revenue"         bColor={C.forest}   priority="high"     onClick={() => openModal("sba")} />
            <InsightCard icon="📉" title="NIM Forecast: -12bps Additional Compression Expected Q2"     body="$100M FHLB advance at 4.28% before May cut protects ~$420K NII annually."                                                    bdg="Rate Risk"       bColor={C.gold}     priority="high"     onClick={() => openModal("nim")} />
            <InsightCard icon="🌾" title="Ag Portfolio: River County Drought Impacts 14 Loans ($67M)"  body="5 of 14 borrowers lack current crop insurance verification. Enhanced monitoring needed by March 15."                          bdg="Credit Watch"    bColor={C.gold}     priority="medium"   onClick={() => openModal("agdrought")} />
          </div>
        }
        right={
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .8 }}>Community Advantage</div>
            {[
              { icon: "⚡", label: "Loan Decision Speed", val: "3.2 days",    note: "vs 18 days big bank",   color: C.forest   },
              { icon: "🤝", label: "Products per HH",     val: "4.1",         note: "vs 2.8 CU average",     color: C.burgundy },
              { icon: "🌱", label: "CRA Rating",          val: "Outstanding", note: "Top 8% in CA",          color: C.forest   },
              { icon: "📞", label: "Local Decisions",     val: "100%",        note: "No out-of-state credit", color: C.gold    },
            ].map((d, i) => (
              <Card key={i} style={{ padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 20 }}>{d.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .5 }}>{d.label}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{d.val}</div>
                  </div>
                  <span style={{ fontSize: 11, color: d.color, fontWeight: 700 }}>{d.note}</span>
                </div>
              </Card>
            ))}
            <button onClick={onEscalate} style={{ background: C.burgundyBg, border: `1px solid ${C.burgundy}30`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: C.burgundy, fontWeight: 700 }}>
              📨 Escalate Alert to Team
            </button>
          </div>
        }
      />
    </div>
  );
}

// ── CFO DASHBOARD ─────────────────────────────────────────────────
function CFODash({ openModal, onEscalate }: { openModal: (m: string) => void; onEscalate: () => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        <KpiCard label="Net Income YTD"  value="$7.9M"   sub="+6.2% vs budget" trend="up" spark={[1.1,2.3,3.9,5.1,6.4,7.9]}      color={C.forest}   />
        <KpiCard label="NIM"             value="3.42%"   sub="-8bps QoQ"       trend="dn" spark={[3.72,3.61,3.55,3.50,3.47,3.42]} color={C.burgundy} onClick={() => openModal("nim")} />
        <KpiCard label="Efficiency Ratio"value="58.3%"   sub="Target: <57%"    trend="dn" spark={[57.1,57.4,57.8,58.0,58.3,58.3]} color={C.gold}     />
        <KpiCard label="Tier 1 Capital"  value="12.8%"   sub="Well-capitalized"           spark={[12.4,12.5,12.6,12.7,12.8,12.8]} color={C.forest}   />
        <KpiCard label="Uninsured Deps"  value="34.1%"   sub="+2.3% since Jan" trend="dn" spark={[31.2,32.4,33.1,33.6,34.1,34.1]} color={C.burgundy} onClick={() => openModal("deposits")} />
      </div>
      <TwoCol
        left={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <InsightCard icon="💧" title="Uninsured Deposits at 34.1% — FDIC Exam Scrutiny Likely"     body="IntraFi CDARS placement of $120M reduces ratio to 30.8%."                                                      bdg="Exam Risk"   bColor={C.burgundy} priority="critical" onClick={() => openModal("deposits")} />
            <InsightCard icon="📋" title="Q1 Close: 3 Items Pending CFO Action — Deadline March 7"    body="CECL Ag Q-factor +$1.2M, deferred loan fee recon $840K, BOLI accrual $1.2M."                                   bdg="Mar 7"       bColor={C.burgundy} priority="critical" onClick={() => openModal("cecl")} />
            <InsightCard icon="🏛️" title="FHLB: $340M Capacity — Pre-Positioning Window"              body="Lock $100M 5-yr at 4.28% before May Fed cut."                                                                   bdg="Opportunity" bColor={C.forest}   priority="medium"   onClick={() => openModal("nim")} />
            <InsightCard icon="📊" title="Efficiency Ratio Rising — Branch Consolidation ROI Ready"   body="West Branch: $0.31 per $1 revenue vs $0.22 system avg. Consolidation saves $420K."                           bdg="Strategic"   bColor={C.gold}     priority="medium" />
          </div>
        }
        right={
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .8 }}>Funding Mix vs. Peers</div>
            {[
              ["Non-Int. DDA",      "28.4%", "24.1%", true ],
              ["IB DDA + Savings",  "31.2%", "29.8%", true ],
              ["MMDA",              "22.7%", "26.3%", false],
              ["Time Deposits",     "11.8%", "14.2%", true ],
              ["Borrowed Funds",     "5.9%",  "5.6%", true ],
            ].map(([l, v, p, g], i) => (
              <Card key={i} style={{ padding: "10px 13px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <span style={{ fontSize: 11, color: C.muted }}>{l}</span>
                  <span style={{ fontSize: 11, color: g ? C.forest : C.burgundy, fontWeight: 700 }}>{g ? "✓" : "↑"} Peer {p}</span>
                </div>
                <Bar val={parseFloat(v as string)} max={40} color={g ? C.forest : C.gold} />
              </Card>
            ))}
            <button onClick={onEscalate} style={{ background: C.burgundyBg, border: `1px solid ${C.burgundy}30`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: C.burgundy, fontWeight: 700 }}>
              📨 Escalate to Team
            </button>
          </div>
        }
      />
    </div>
  );
}

// ── BANKER DASHBOARD ──────────────────────────────────────────────
function BankerDash({ openModal, onEscalate }: { openModal: (m: string) => void; onEscalate: () => void }) {
  const rc: Record<string, string> = { high: C.burgundy, medium: C.gold, low: C.forest };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        <KpiCard label="Portfolio Loans"   value="$47.3M" sub="+$2.1M MTD"    trend="up" spark={[43.1,44.2,45.1,45.8,46.4,47.3]} color={C.burgundy} onClick={() => openModal("portfolioloans")} />
        <KpiCard label="Deposits Managed"  value="$28.1M" sub="-$340K MTD"    trend="dn" spark={[28.8,28.6,28.4,28.3,28.2,28.1]} color={C.gold}     />
        <KpiCard label="Avg Cross-Sell"    value="3.8"    sub="Target: 4.5 prods" trend="dn"                                      color={C.rust}     onClick={() => openModal("atriskrev")} />
        <KpiCard label="At-Risk Revenue"   value="$218K"  sub="5 relationships"  trend="dn"                                       color={C.burgundy} onClick={() => openModal("customeratrisk")} />
      </div>
      <TwoCol
        ratio="3fr 2fr"
        left={
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .8, marginBottom: 10 }}>Next Best Actions — AI Prioritized</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {TEST_DATA.crm.relationships.map((c, i) => (
                <Card key={i} style={{ padding: 0, overflow: "hidden" }} onClick={() => openModal("atriskrev")}>
                  <div style={{ display: "flex" }}>
                    <div style={{ width: 3, background: rc[c.risk], flexShrink: 0 }} />
                    <div style={{ padding: "12px 14px", flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 9, background: `${rc[c.risk]}12`, border: `1px solid ${rc[c.risk]}25`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: rc[c.risk], flexShrink: 0 }}>
                        {c.name[0]}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{c.name}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>${(c.loan / 1000000).toFixed(1)}M</span>
                        </div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                          {c.type} · Score: <span style={{ color: c.score > 80 ? C.forest : c.score > 70 ? C.gold : C.burgundy, fontWeight: 700 }}>{c.score}</span>
                        </div>
                        <div style={{ fontSize: 11, color: rc[c.risk], marginTop: 4, fontWeight: 600 }}>→ {c.action}</div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        }
        right={
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <InsightCard icon="🏘️" title="3 New Business Licenses This Week"   body="Reedley Dental, Valley Tire & Lube, Coastal Imports — all match SBA profile." bdg="Prospects"  bColor={C.forest}   priority="medium"   onClick={() => openModal("prospects")} />
            <InsightCard icon="🎯" title="Competitor Activity: Sunrise Medical" body="Wells Fargo credit inquiry Feb 24. Outreach within 48 hrs."                    bdg="Retention"  bColor={C.burgundy} priority="critical" onClick={() => openModal("competitor")} />
            <button onClick={onEscalate} style={{ background: C.burgundyBg, border: `1px solid ${C.burgundy}30`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: C.burgundy, fontWeight: 700 }}>
              📨 Escalate to Credit / Compliance
            </button>
          </div>
        }
      />
    </div>
  );
}

// ── OPS DASHBOARD ─────────────────────────────────────────────────
function OpsDash({ openModal, onEscalate }: { openModal: (m: string) => void; onEscalate: () => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        <KpiCard label="ACH Exceptions"  value="7"     sub="3 critical today"      trend="dn" color={C.burgundy} onClick={() => openModal("ach")}    />
        <KpiCard label="Wires Today"     value="43"    sub="$12.4M · 1 OFAC hold"             color={C.gold}     onClick={() => openModal("ofac")}   />
        <KpiCard label="Teller Error Rate"value="0.04%"sub="Target: <0.08%"         trend="up" color={C.forest}                                       />
        <KpiCard label="Open IT Tickets" value="18"    sub="4 escalated"            trend="dn" color={C.gold}                                          />
        <KpiCard label="Reg CC Training" value="61%"   sub="87 tellers pending"     trend="dn" color={C.burgundy} onClick={() => openModal("regcc")}  />
      </div>
      <TwoCol
        left={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <InsightCard icon="🔴" title="ACH Return Rate Spike: 2.4% vs 1.5% Threshold"             body="6 returns from 2 Metro employers — possible payroll funding issue."                   bdg="Investigate Now" bColor={C.burgundy} priority="critical" onClick={() => openModal("ach")}   />
            <InsightCard icon="🔒" title="North Branch Vault Overage: $4,200 — Dual Control Required"      body="Vault $4,200 over system balance. Last balanced Feb 28."                               bdg="Discrepancy"     bColor={C.gold}     priority="high"     onClick={() => openModal("vault")}  />
            <InsightCard icon="🌐" title="OFAC Wire Hold: Al Rashid Trading — 94% SDN Match"         body="$28,400 outbound wire held. BSA review closes in 2 hours."                             bdg="Immediate"       bColor={C.burgundy} priority="critical" onClick={() => openModal("ofac")}  />
            <InsightCard icon="⚙️" title="Core Maintenance: March 7, 11 PM–3 AM"                    body="Notify all 18 branches by March 5. Online banking read-only 12:30–1:30 AM."            bdg="Scheduled"       bColor={C.rust}     priority="low"                                         />
            <InsightCard icon="📚" title="Reg CC Training: 61% Complete — 7 Days to Deadline"        body="87 tellers outstanding. North and Regional Hub branches worst performers."                  bdg="Mar 9"           bColor={C.burgundy} priority="high"     onClick={() => openModal("regcc")} />
          </div>
        }
        right={
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { name: "Main Branch",    score: 98, s: "ok",     note: "All clear"      },
              { name: "North Branch",  score: 79, s: "high",   note: "Vault overage"  },
              { name: "Regional Hub",  score: 76, s: "high",   note: "Error 0.09%"    },
              { name: "West Branch",   score: 92, s: "medium", note: "Low L/D"        },
              { name: "Commerce Branch", score: 97, s: "ok",   note: "All clear"      },
            ].map((b, i) => (
              <Card key={i} style={{ padding: "10px 13px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <SDot s={b.s} />
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: C.text }}>{b.name}</span>
                  <span style={{ fontSize: 11, color: C.muted }}>{b.note}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: b.score >= 90 ? C.forest : b.score >= 80 ? C.gold : C.burgundy, marginLeft: 8 }}>{b.score}</span>
                </div>
              </Card>
            ))}
            <button onClick={onEscalate} style={{ background: C.burgundyBg, border: `1px solid ${C.burgundy}30`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: C.burgundy, fontWeight: 700 }}>
              📨 Escalate to Compliance
            </button>
          </div>
        }
      />
    </div>
  );
}

// ── COMPLIANCE DASHBOARD ──────────────────────────────────────────
function ComplianceDash({ openModal, onEscalate }: { openModal: (m: string) => void; onEscalate: () => void }) {
  const calItems: [string, string, boolean, (() => void) | null][] = [
    ["Mar 2",  "SAR Filing — #4471",      false, () => openModal("sar")  ],
    ["Mar 7",  "Call Report Q4 Final",    true,  null                    ],
    ["Mar 9",  "Reg CC Teller Training",  false, null                    ],
    ["Mar 15", "HMDA LAR Corrections",    false, () => openModal("hmda") ],
    ["Mar 31", "BSA Board Report",        true,  null                    ],
    ["May 1",  "FDIC Risk Exam",          false, null                    ],
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        <KpiCard label="Open SARs (YTD)" value="4"           sub="1 draft pending"  trend="dn" color={C.burgundy} onClick={() => openModal("sar")}        />
        <KpiCard label="CTR Filed (Feb)"  value="23"          sub="100% on time"     trend="up" color={C.forest}                                           />
        <KpiCard label="CRA Rating"       value="Outstanding" sub="Exam: Q4 2026"              color={C.forest}   onClick={() => openModal("cra")}        />
        <KpiCard label="HMDA Exceptions"  value="6"           sub="Due March 15"     trend="dn" color={C.burgundy} onClick={() => openModal("hmda")}       />
        <KpiCard label="BSA Training"     value="91%"         sub="Target 100% Apr"  trend="up" color={C.gold}                                             />
      </div>
      <TwoCol
        left={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <InsightCard icon="🚨" title="SAR Candidate: Structuring — Account #4471 (File TODAY)"  body="J. Smith DBA Metro Imports: 8 cash deposits $8,750–$9,800 over 14 days across 4 branches." bdg="File Today" bColor={C.burgundy} priority="critical" onClick={() => openModal("sar")}        />
            <InsightCard icon="🌍" title="OFAC: Al Rashid Trading — 94% SDN Match, Wire Held"      body="$28,400 outbound wire flagged. BSA review closes in 2 hours."                                        bdg="Immediate"  bColor={C.burgundy} priority="critical" onClick={() => openModal("ofac")}       />
            <InsightCard icon="📋" title="HMDA: 6 Exceptions — LAR Blocked Until March 15"         body="4 in-person GMI forms missing. 2 mail-in issues. Bankers alerted."                                   bdg="Mar 15"     bColor={C.gold}     priority="high"     onClick={() => openModal("hmda")}       />
            <InsightCard icon="🔍" title="14 High-Risk Customer Annual Reviews Overdue"             body="3 MSBs, 4 cannabis-adjacent, 7 international wire customers."                                        bdg="Overdue"    bColor={C.burgundy} priority="high"     onClick={() => openModal("bsareviews")} />
          </div>
        }
        right={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .8 }}>90-Day Regulatory Calendar</div>
            {calItems.map(([d, item, ok, action], i) => {
              const rel = relDate(d);
              return (
                <div
                  key={i}
                  onClick={action ?? undefined}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", background: rel.isPast ? C.surface : C.card, border: `1px solid ${rel.isToday ? C.burgundy + "60" : ok ? C.border : C.burgundy + "25"}`, borderRadius: 8, cursor: action ? "pointer" : "default", opacity: rel.isPast ? .55 : 1 }}
                  onMouseEnter={e => action && ((e.currentTarget as HTMLElement).style.background = C.surface)}
                  onMouseLeave={e => ((e.currentTarget as HTMLElement).style.background = rel.isPast ? C.surface : C.card)}
                >
                  <div style={{ width: 44, flexShrink: 0, textAlign: "center" }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: rel.color, textDecoration: rel.isPast ? "line-through" : "none" }}>{d}</div>
                    {rel.label && <div style={{ fontSize: 9, color: rel.isToday ? C.burgundy : rel.color, fontWeight: rel.isToday ? 800 : 600, marginTop: 1 }}>{rel.label}</div>}
                  </div>
                  <span style={{ fontSize: 11, color: rel.isPast ? C.muted : C.text, flex: 1, textDecoration: rel.isPast ? "line-through" : "none" }}>{item}</span>
                  <SDot s={ok ? "ok" : rel.isToday ? "critical" : "high"} />
                </div>
              );
            })}
            <button onClick={onEscalate} style={{ background: C.burgundyBg, border: `1px solid ${C.burgundy}30`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: C.burgundy, fontWeight: 700 }}>
              📨 Escalate to Operations / Credit
            </button>
          </div>
        }
      />
    </div>
  );
}

// ── CREDIT DASHBOARD ──────────────────────────────────────────────
function CreditDash({ openModal, onEscalate }: { openModal: (m: string) => void; onEscalate: () => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        <KpiCard label="NPL Ratio"         value="0.61%" sub="Peer avg 0.74%"   trend="up" spark={[0.74,0.71,0.68,0.65,0.63,0.61]} color={C.forest}   onClick={() => openModal("npl")}  />
        <KpiCard label="Criticized Loans"  value="$47.3M"sub="14 credits"       trend="dn" spark={[38,40,42,44,46,47.3]}           color={C.gold}     onClick={() => openModal("npl")}  />
        <KpiCard label="CECL Reserve"      value="1.42%" sub="+4bps QoQ"        trend="dn" spark={[1.31,1.33,1.36,1.38,1.40,1.42]} color={C.gold}     onClick={() => openModal("ceclscenarios")} />
        <KpiCard label="Net Charge-Offs"   value="0.08%" sub="Target <0.15%"    trend="up" spark={[0.12,0.10,0.09,0.09,0.08,0.08]} color={C.forest}   />
        <KpiCard label="CRE Concentration" value="287%"  sub="Threshold: 300%"  trend="dn" spark={[271,274,278,282,285,287]}        color={C.gold}     onClick={() => openModal("cre")}  />
      </div>
      <TwoCol
        left={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <InsightCard icon="🚨" title="Oaks Street Properties: $6.7M Maturity April 16 — DSCR 1.11x"   body="DSCR declined from 1.38x at origination. LTV 72%. Paydown or guarantor support discussion needed."     bdg="Action Required"   bColor={C.burgundy} priority="critical" onClick={() => openModal("oaks")}     />
            <InsightCard icon="📊" title="Valley Ridge Growers: Substandard Migration Likely Q1"           body="LOC utilization 96% for 3 months. Revenue -18% YoY. CECL impact +$180K."                              bdg="Downgrade Pending" bColor={C.burgundy} priority="high"     onClick={() => openModal("npl")}      />
            <InsightCard icon="🌾" title="Ag Drought: 5 of 14 Borrowers Lack Insurance Verification"       body="River County USDA D3 designation. $31M unverified insurance. March 15 deadline."                      bdg="Credit Watch"      bColor={C.gold}     priority="high"     onClick={() => openModal("agdrought")} />
            <InsightCard icon="✅" title="17 Annual Reviews Due — AI Pre-Screen Saves 42 Hours"            body="11 clean, 4 need financials, 2 full re-underwrite. Analysis ready for review."                         bdg="Efficiency"        bColor={C.forest}   priority="low"                                         />
          </div>
        }
        right={
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .8 }}>Portfolio Mix</div>
            {[
              ["CRE Non-Owner", "$1.04B", 36, C.burgundy],
              ["C&I",           "$612M",  21, C.rust    ],
              ["Agricultural",  "$438M",  15, C.gold    ],
              ["Owner-Occ CRE", "$349M",  12, C.forest  ],
              ["Residential",   "$262M",   9, C.cyan    ],
              ["Consumer",      "$155M",   5, C.dim     ],
            ].map(([l, v, p, c], i) => (
              <Card key={i} style={{ padding: "10px 13px", cursor: "pointer" }} onClick={() => openModal("cre")}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <span style={{ fontSize: 11, color: C.muted }}>{l}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{v}</span>
                </div>
                <Bar val={p as number} color={c as string} />
              </Card>
            ))}
            <button onClick={onEscalate} style={{ background: C.burgundyBg, border: `1px solid ${C.burgundy}30`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: C.burgundy, fontWeight: 700 }}>
              📨 Escalate to CFO / Compliance
            </button>
          </div>
        }
      />
    </div>
  );
}

// ── LENDING DASHBOARD ─────────────────────────────────────────────
function LendingDash({ openModal, onEscalate }: { openModal: (m: string) => void; onEscalate: () => void }) {
  const stageColor: Record<string, string> = {
    "Approved": C.forest, "Committee": C.gold, "Underwriting": C.rust,
    "Processing": C.gold, "Pre-approval": C.cyan, "Term Sheet": C.cyan, "SBA Review": C.muted,
  };
  const totalPipeline = LENDING_PIPELINE.reduce((s, l) => s + l.amount, 0);
  const stalled = LENDING_PIPELINE.filter(l => l.issue !== null).length;
  const approved = LENDING_PIPELINE.filter(l => l.stage === "Approved").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        <KpiCard label="Pipeline Total"   value="$14.1M"  sub="8 active opportunities"           spark={[10.2,11.4,12.1,13.0,13.6,14.1]} color={C.cyan}     onClick={() => openModal("lendingpipeline")} />
        <KpiCard label="Approved / Close" value={approved} sub="Ready to close this month"  trend="up"                                   color={C.forest}   onClick={() => openModal("lendingpipeline")} />
        <KpiCard label="Stalled Items"    value={stalled}  sub="Have open issues"           trend="dn"                                   color={C.burgundy} onClick={() => openModal("lendingpipeline")} />
        <KpiCard label="SBA Pipeline"     value="$42M"     sub="34% below CA peer median"   trend="dn" spark={[48,46,44,43,42,42]}        color={C.gold}     onClick={() => openModal("sba")} />
        <KpiCard label="Pull-Through"     value="76%"      sub="Target: 80%"                trend="dn" spark={[82,80,79,78,77,76]}        color={C.rust}     onClick={() => openModal("lendingpipeline")} />
      </div>
      <TwoCol
        ratio="3fr 2fr"
        left={
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .8, marginBottom: 10 }}>Active Pipeline — Priority Order</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {LENDING_PIPELINE.map((lp, i) => (
                <Card key={i} style={{ padding: 0, overflow: "hidden" }} onClick={() => openModal("lendingpipeline")}>
                  <div style={{ display: "flex" }}>
                    <div style={{ width: 3, background: lp.issue ? C.burgundy : C.forest, flexShrink: 0 }} />
                    <div style={{ padding: "11px 14px", flex: 1, display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 12, alignItems: "center" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 12, color: C.text }}>{lp.borrower}</div>
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{lp.id} · {lp.type} · {lp.rm}</div>
                        {lp.issue && <div style={{ fontSize: 10, color: C.burgundy, fontWeight: 600, marginTop: 3 }}>⚠ {lp.issue}</div>}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>${(lp.amount / 1e6).toFixed(1)}M</div>
                      <div style={{ fontSize: 10, color: stageColor[lp.stage] || C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5 }}>{lp.stage}</div>
                      <div style={{ fontSize: 10, color: lp.daysIn > 20 ? C.burgundy : lp.daysIn > 10 ? C.gold : C.muted }}>{lp.daysIn}d in stage</div>
                      <div style={{ fontSize: 10, color: C.dim }}>{lp.closeTarget}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        }
        right={
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <InsightCard icon="🚨" title="LP-006 Martinez Trucking Approved — Title Search Blocking Close"  body="$480K equipment loan approved Mar 1. Title search outstanding. Close date Mar 10 — 8 days away. Escalate to title company today."            bdg="Close at Risk"   bColor={C.burgundy} priority="critical" onClick={() => openModal("lendingpipeline")} />
            <InsightCard icon="📋" title="LP-004 Committee Today — Borrowing Base Needed Before 2 PM"       body="Central Valley Orchards $1.2M Ag LOC in credit committee at 2pm. Borrowing base calculation still outstanding from RM."                     bdg="Today"           bColor={C.burgundy} priority="critical" onClick={() => openModal("lendingpipeline")} />
            <InsightCard icon="🏛️" title="SBA Pipeline: $42M — 7 Pre-Qualified, Fees Available Q1"         body="7 clients pre-qualified with on-file financials. Est. $420K in SBA guarantee fees this quarter if closed."                                  bdg="Revenue"         bColor={C.forest}   priority="high"     onClick={() => openModal("sba")} />
            <InsightCard icon="🏢" title="CRE Concentration 287% — 4 Pipeline Deals Need ALCO Waiver"      body="LP-001 and LP-005 combined push concentration to 298% of capital. ALCO pre-approval required before issuing binding term sheets."            bdg="Limit Risk"      bColor={C.gold}     priority="high"     onClick={() => openModal("cre")} />
            <button onClick={onEscalate} style={{ background: C.cyanBg || `${C.cyan}12`, border: `1px solid ${C.cyan}30`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: C.cyan, fontWeight: 700 }}>
              📨 Escalate to Credit / ALCO
            </button>
          </div>
        }
      />
    </div>
  );
}

// ── CRO / ALCO DASHBOARD ──────────────────────────────────────────
function CRODash({ openModal, onEscalate }: { openModal: (m: string) => void; onEscalate: () => void }) {
  const CRO_COLOR = "#1E3A5F";
  const ip = INVESTMENT_PORTFOLIO;
  const lm = LIQUIDITY_METRICS;
  const cd = CD_MATURITY_LADDER;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        <KpiCard label="LCR (Base 30-day)" value="182%"   sub="Min: 100%"            trend="up"  spark={[164,168,172,178,181,182]} color={C.forest}   onClick={() => openModal("lcr")} />
        <KpiCard label="AFS Unrealized"    value="-$28.4M" sub="AOCI -$27.2M"        trend="dn"  spark={[-18,-21,-24,-26,-27,-28]} color={C.burgundy} onClick={() => openModal("investments")} />
        <KpiCard label="EVE +200bps"       value="+$12.4M" sub="Within policy"        trend="up"  spark={[10.1,10.8,11.2,11.8,12.1,12.4]} color={C.forest} />
        <KpiCard label="AFS Duration"      value="5.8yr"   sub="Policy limit: 6yr"   trend="dn"  spark={[5.1,5.3,5.5,5.6,5.7,5.8]} color={C.gold}   onClick={() => openModal("investments")} />
        <KpiCard label="CD Repricing Risk" value="$140M"   sub="Due < 6 months"      trend="dn"  spark={[95,104,118,128,136,140]} color={C.burgundy} onClick={() => openModal("cdladder")} />
      </div>
      <TwoCol
        ratio="3fr 2fr"
        left={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <InsightCard icon="🏛️" title="FHLB $100M 5-yr Lock — Window Closes Before May FOMC"              body="4.28% all-in rate protects $420K NII annually vs. base-case rate-cut scenario. ALCO vote required March 14. Every 25bps cut = ~$1.1M annual NII impact." bdg="Action Required" bColor={CRO_COLOR}  priority="critical" onClick={() => openModal("nim")} />
            <InsightCard icon="📉" title="AFS Portfolio: $28.4M Unrealized Loss — SVB-Parallel Exam Focus"   body="Post-SVB, FDIC examiners scrutinize AFS unrealized losses in all community bank exams. AOCI -$27.2M flows through Tier 1 under DFAST. Duration 5.8yr approaching 6yr policy ceiling."  bdg="Exam Risk" bColor={C.burgundy} priority="high"     onClick={() => openModal("investments")} />
            <InsightCard icon="💧" title="Correlated-Exit Stress: LCR Drops to 88% — Below 100% Floor"       body="Metro Motors + Regional Ag Cooperative correlated exit drives $420M 30-day outflow. Combined HQLA + FHLB covers 110%. Thin margin for FDIC liquidity test." bdg="Liquidity Risk" bColor={C.gold}   priority="high"     onClick={() => openModal("lcr")} />
            <InsightCard icon="📊" title="CD Maturity: $140M Repricing < 6 Months — Rate Cut Risk"           body="$48M matures in under 3 months at avg 4.85%. If Fed cuts 50bps by June, repricing these CDs saves ~$240K annually — opportunity to extend duration at current rates." bdg="Opportunity" bColor={C.forest} priority="medium"   onClick={() => openModal("cdladder")} />
            <button onClick={onEscalate} style={{ background: `${CRO_COLOR}12`, border: `1px solid ${CRO_COLOR}30`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: CRO_COLOR, fontWeight: 700 }}>📨 Escalate to ALCO / CFO</button>
          </div>
        }
        right={
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .8 }}>NII Rate Sensitivity</div>
            {TEST_DATA.alm.rateSensitivity.map((s, i) => (
              <Card key={i} style={{ padding: "10px 13px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{s.scenario}</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: s.niiImpact >= 0 ? C.forest : C.burgundy }}>{s.niiImpact >= 0 ? "+" : ""}${(s.niiImpact/1e6).toFixed(1)}M</span>
                </div>
                <Bar val={Math.abs(s.pct * 100)} max={25} color={s.niiImpact >= 0 ? C.forest : C.burgundy} />
              </Card>
            ))}
          </div>
        }
      />
    </div>
  );
}

// ── BRANCH MANAGER DASHBOARD ──────────────────────────────────────
function BranchMgrDash({ openModal, onEscalate }: { openModal: (m: string) => void; onEscalate: () => void }) {
  const BRANCH_COLOR = "#7A4F1D";
  const myBranch = BRANCH_DATA[0]; // Main Branch — Lee Todd's branch
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        <KpiCard label="Branch Deposits"    value="$684M"   sub={`Goal: $700M`}            trend="dn" spark={[640,652,661,668,676,684]} color={C.gold}     onClick={() => openModal("branchdrill")} />
        <KpiCard label="Revenue / Teller"   value="$742"    sub="#1 in network"             trend="up" spark={[680,695,710,722,734,742]} color={C.forest}   onClick={() => openModal("branchdrill")} />
        <KpiCard label="Teller Transactions" value="9,820"  sub="per teller / month"        trend="up" spark={[8820,9080,9320,9540,9710,9820]} color={C.forest} />
        <KpiCard label="New DDA Accounts"   value="84"      sub="Target: 120 / quarter"     trend="dn" spark={[12,22,38,52,68,84]} color={C.gold} />
        <KpiCard label="Error Rate"         value="0.021%"  sub="< 0.05% target"            trend="up" spark={[0.042,0.038,0.032,0.028,0.024,0.021]} color={C.forest} />
      </div>
      <TwoCol
        ratio="3fr 2fr"
        left={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <InsightCard icon="🚨" title="Vault Camera Offline — North Branch (INC-2215 SLA Breached)"  body="North Branch vault camera offline since Feb 23. IT ticket INC-2215 exceeded 4-hr SLA (now 108 hours). Physical security gap — dual-control procedures required until resolved." bdg="Security" bColor={C.burgundy} priority="critical" />
            <InsightCard icon="📉" title="Q1 Deposit Goal: $40M Behind — 28 Days Remaining"              body="System-wide deposit goal shortfall. Main Branch leading the network at $684M but $16M short. CD special at 4.85% for $500K+ accounts projected to capture $22M." bdg="Q1 Goal"  bColor={C.gold}    priority="high"     onClick={() => openModal("cdladder")} />
            <InsightCard icon="📚" title="Reg CC Training: 7 Tellers Not Certified — Deadline March 9"   body="7 tellers have not completed Reg CC hold policy update. Cornerstone LMS shows deadline in 7 days. FDIC examiner will specifically review compliance cert completion." bdg="Mar 9"    bColor={C.burgundy} priority="high"     />
            <InsightCard icon="⭐" title="Commerce Branch Best Practice: $814 Revenue/Teller — Peer Coaching Opp" body="Commerce Branch manager D. North achieves 38% higher transaction complexity mix (commercial vs. consumer). Peer-to-peer coaching program estimated +$180K annual revenue lift for West Branch." bdg="Strategy" bColor={C.forest}  priority="medium"   onClick={() => openModal("branchdrill")} />
            <button onClick={onEscalate} style={{ background: `${BRANCH_COLOR}12`, border: `1px solid ${BRANCH_COLOR}30`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: BRANCH_COLOR, fontWeight: 700 }}>📨 Escalate to Operations</button>
          </div>
        }
        right={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .8, marginBottom: 2 }}>Branch Health — Network View</div>
            {BRANCH_DATA.map((b, i) => (
              <Card key={i} style={{ padding: "10px 13px", cursor: "pointer" }} onClick={() => openModal("branchdrill")}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{b.name}</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: b.healthScore >= 85 ? C.forest : b.healthScore >= 70 ? C.gold : C.burgundy }}>{b.healthScore}</span>
                </div>
                <Bar val={b.healthScore} max={100} color={b.healthScore >= 85 ? C.forest : b.healthScore >= 70 ? C.gold : C.burgundy} />
                <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>${(b.deposits/1e6).toFixed(0)}M deposits · ${b.revenuePerTeller}/teller</div>
              </Card>
            ))}
          </div>
        }
      />
    </div>
  );
}

// ── MORTGAGE / CONSUMER DASHBOARD ────────────────────────────────
function MortgageDash({ openModal, onEscalate }: { openModal: (m: string) => void; onEscalate: () => void }) {
  const MORT_COLOR = "#6B3D8A";
  const pipeline = MORTGAGE_PIPELINE;
  const totalPipeline = pipeline.reduce((s, m) => s + m.amount, 0);
  const purchase = pipeline.filter(m => m.purpose === "Purchase");
  const refi     = pipeline.filter(m => m.purpose === "Refi");
  const lmiCount = pipeline.filter(m => m.lmi).length;
  const expiringSoon = pipeline.filter(m => m.daysToLock <= 14).length;
  const statusColor: Record<string,string> = { Approved: C.forest, Underwriting: C.gold, Processing: C.gold, "Conditional": C.gold, "Docs Ordered": C.forest, "Pre-approval": C.forest, "SBA Review": C.muted, "Suspended": C.burgundy, "Approved": C.forest };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        <KpiCard label="Mortgage Pipeline"  value={`$${(totalPipeline/1e6).toFixed(1)}M`} sub={`${pipeline.length} loans`} trend="up" spark={[2.1,2.6,3.0,3.4,3.7,3.9]} color={MORT_COLOR} onClick={() => openModal("mortpipeline")} />
        <KpiCard label="Purchase / Refi"    value={`${purchase.length}/${refi.length}`}    sub="purchase-heavy"              trend="up" spark={[3,4,4,5,5,5]}              color={C.forest}  />
        <KpiCard label="Locks Expiring ≤14d" value={expiringSoon}                          sub="need to clear UW"            trend="dn" spark={[1,1,2,2,3,3]}              color={C.burgundy} onClick={() => openModal("mortpipeline")} />
        <KpiCard label="LMI Borrowers"      value={lmiCount}                               sub="CRA-qualifying"              trend="up" spark={[1,1,2,2,3,3]}              color={C.forest}  />
        <KpiCard label="HMDA Errors"        value="2"                                      sub="Due Mar 31"                  trend="dn" spark={[0,0,0,0,1,2]}              color={C.gold}    onClick={() => openModal("hmdadetail")} />
      </div>
      <TwoCol
        ratio="3fr 2fr"
        left={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <InsightCard icon="🚨" title="MG-007 Williams Suspended — ECOA Adverse Action Due TODAY"   body="File suspended day 30. ECOA requires adverse action notice within 30 days of application. Failure to send today creates fair lending violation. Denial reasons: (1) Insufficient income, (2) Credit score, (3) DTI ratio." bdg="ECOA Risk"  bColor={C.burgundy} priority="critical" onClick={() => openModal("mortpipeline")} />
            <InsightCard icon="🔒" title="MG-001 Rodriguez Lock Expires Mar 15 — UW Conditions Open"  body="Conventional 30yr, $485K, 6.875%. Lock expires in 13 days. 2 UW conditions still open: (1) Gift letter from parents, (2) HOA cert. Estimated 3 days to clear — within window." bdg="Lock Risk"   bColor={C.gold}    priority="high"     onClick={() => openModal("mortpipeline")} />
            <InsightCard icon="📊" title="Rate Drop 12bps — 82 Borrowers on Refi Eligibility List"    body="Current 30yr at 6.74%. 82 existing customers have 7.50%+ rate with adequate equity. Estimated $4.8M origination volume. Average pull-through on outreach: 34%. Automated rate alert triggers available." bdg="Revenue"     bColor={C.forest}  priority="high"     />
            <InsightCard icon="⚖️" title="HMDA Q1 LAR: 2 Corrections Required Before March 31"        body="MG-007: Adverse action reason code error (needs 3-code format). MG-003: Non-taxable Social Security income not grossed up per Reg C. Both correctable in 10 minutes in HMDA filing software." bdg="Mar 31"     bColor={C.gold}    priority="medium"   onClick={() => openModal("hmdadetail")} />
            <button onClick={onEscalate} style={{ background: `${MORT_COLOR}12`, border: `1px solid ${MORT_COLOR}30`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: MORT_COLOR, fontWeight: 700 }}>📨 Escalate to Compliance</button>
          </div>
        }
        right={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .8, marginBottom: 2 }}>Pipeline — Lock Expiry View</div>
            {pipeline.slice(0,6).map((m, i) => (
              <Card key={i} style={{ padding: "10px 13px", cursor: "pointer" }} onClick={() => openModal("mortpipeline")}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.borrower}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{m.purpose} · {m.type} · ${(m.amount/1e3).toFixed(0)}K</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: m.daysToLock <= 14 ? C.burgundy : m.daysToLock <= 21 ? C.gold : C.muted }}>Lock: {m.daysToLock}d</div>
                    <div style={{ fontSize: 9, color: statusColor[m.status] || C.muted, fontWeight: 700 }}>{m.status}</div>
                  </div>
                </div>
                {m.lmi && <div style={{ marginTop: 3, fontSize: 9, color: C.forest, fontWeight: 700 }}>● CRA/LMI</div>}
              </Card>
            ))}
          </div>
        }
      />
    </div>
  );
}

// ── WEALTH / TRUST DASHBOARD ──────────────────────────────────────
function WealthDash({ openModal, onEscalate }: { openModal: (m: string) => void; onEscalate: () => void }) {
  const WEALTH_COLOR = "#2C6B5F";
  const w = WEALTH_DATA;
  const prioColor: Record<string,string> = { critical: C.burgundy, high: C.gold, medium: C.rust };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        <KpiCard label="Total AUM"         value="$284M"  sub="+4.2% QoQ"             trend="up" spark={[248,256,264,272,279,284]} color={WEALTH_COLOR}  onClick={() => openModal("wealthaum")} />
        <KpiCard label="Fee Income MTD"    value="$198K"  sub="$2.84M annual pace"    trend="up" spark={[148,162,172,182,191,198]} color={C.forest}     />
        <KpiCard label="Accounts"          value="142"    sub="98 discretionary"       trend="up" spark={[128,132,136,139,141,142]} color={WEALTH_COLOR}  onClick={() => openModal("wealthaum")} />
        <KpiCard label="Fiduciary Alerts"  value={w.fiduciaryAlerts.length}  sub="2 require action" trend="dn" spark={[1,1,2,2,3,4]} color={C.burgundy} onClick={() => openModal("wealthaum")} />
        <KpiCard label="Reviews Overdue"   value="1"      sub="Garcia Trust +12d"     trend="dn" spark={[0,0,0,0,0,1]} color={C.gold} onClick={() => openModal("wealthaum")} />
      </div>
      <TwoCol
        ratio="3fr 2fr"
        left={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {w.fiduciaryAlerts.map((a, i) => (
              <InsightCard key={i}
                icon={a.priority === "critical" ? "🚨" : a.priority === "high" ? "⚠️" : "📋"}
                title={`${a.type}: ${a.account}`}
                body={a.detail || `Fiduciary action required — ${a.type.toLowerCase()}`}
                bdg={a.priority} bColor={prioColor[a.priority] || C.muted} priority={a.priority}
                onClick={() => openModal("wealthaum")}
              />
            ))}
            <InsightCard icon="💡" title="7 Commercial Clients Eligible for Wealth Referral — $14M AUM Opportunity" body="Martinez Family Trust ($4.2M loan), Sunrise Medical ($2.2M deposits), Valley Dental ($850K assets) — all meet wealth eligibility thresholds. Estimated $70K annual fee income." bdg="Revenue" bColor={C.forest} priority="medium" onClick={() => openModal("wealthaum")} />
            <button onClick={onEscalate} style={{ background: `${WEALTH_COLOR}12`, border: `1px solid ${WEALTH_COLOR}30`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: WEALTH_COLOR, fontWeight: 700 }}>📨 Escalate to Compliance</button>
          </div>
        }
        right={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .8, marginBottom: 2 }}>Asset Allocation</div>
            {w.assetAllocation.map((a, i) => (
              <Card key={i} style={{ padding: "10px 13px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <span style={{ fontSize: 11, color: C.muted }}>{a.cls}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>${(a.val/1e6).toFixed(1)}M · {(a.pct*100).toFixed(0)}%</span>
                </div>
                <Bar val={a.pct * 100} max={50} color={WEALTH_COLOR} />
              </Card>
            ))}
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .8, marginTop: 6, marginBottom: 2 }}>Top Accounts</div>
            {w.accounts_list.slice(0,3).map((acc, i) => (
              <Card key={i} style={{ padding: "10px 13px", cursor: "pointer" }} onClick={() => openModal("wealthaum")}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{acc.name}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{acc.type} · ${(acc.aum/1e6).toFixed(1)}M</div>
                  </div>
                  <Bdg label={acc.status} color={acc.status === "Current" ? C.forest : acc.status === "At Risk" ? C.burgundy : C.gold} />
                </div>
              </Card>
            ))}
          </div>
        }
      />
    </div>
  );
}

// ── MODAL ROUTER ──────────────────────────────────────────────────
const MODAL_MAP: Record<string, React.FC<{ onClose: () => void }>> = {
  deposits:      DepositConcentrationDetail,
  sba:           SBAPipelineDetail,
  nim:           NIMDetail,
  cecl:          CECLDetail,
  cre:           CREDetail,
  npl:           NPLDetail,
  atriskrev:     AtRiskRevenueDetail,
  portfolioloans:PortfolioLoansDetail,
  competitor:    CompetitorDetail,
  prospects:     ProspectsDetail,
  ach:           ACHDetail,
  ofac:          OFACDetail,
  vault:         VaultDetail,
  regcc:         RegCCDetail,
  sar:           SARDetail,
  cra:           CRADetail,
  hmda:          HMDADetail,
  bsareviews:    BSAReviewsDetail,
  oaks:            OaksStreetDetail,
  agdrought:       AgDroughtDetail,
  ceclscenarios:   CECLScenariosDetail,
  lendingpipeline: LendingPipelineDetail,
  customeratrisk:  CustomerAtRiskDetail,
  investments:     InvestmentPortfolioDetail,
  cdladder:        CDLadderDetail,
  lcr:             LCRDetail,
  loanfile:        LoanFileDetail,
  watchlist:       WatchlistCommitteeDetail,
  loanmod:         LoanModificationDetail,
  agcrops:         AgCropConcentrationDetail,
  mortpipeline:    MortgagePipelineDetail,
  wealthaum:       WealthAUMDetail,
  branchdrill:     BranchDrilldownDetail,
  hmdadetail:      HMDADetail,
  depoprofit:      DepositProfitabilityDetail,
  hmdalar:         HMDALARDetail,
  fairlending:     FairLendingDetail,
  derivatives:     DerivativesDetail,
  modelgov:        ModelGovernanceDetail,
};

// ── DASHBOARD CONTENT ─────────────────────────────────────────────
export function DashboardContent({
  pid, persona, onLog, onEscalate, escalations, onNavTasks, initialModal,
}: {
  pid: string;
  persona: Persona;
  onLog: (action: string, detail: string, category: string) => void;
  onEscalate: () => void;
  escalations: Escalation[];
  onNavTasks?: () => void;
  initialModal?: string;
}) {
  const [modal, setModal] = useState<string | null>(initialModal ?? null);

  const openModal = (name: string) => { setModal(name); onLog("Viewed detail", name, "Dashboard"); };
  const closeModal = () => setModal(null);

  const myEscalations = escalations.filter(e => e.to === pid);

  const ModalComponent = modal ? MODAL_MAP[modal] : null;

  const dashProps = { openModal, onEscalate };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <PriorityBanner pid={pid} onEscalate={onEscalate} onNavTasks={onNavTasks} />
      <MorningBriefing pid={pid} persona={persona} />

      {/* Incoming escalations */}
      {myEscalations.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {myEscalations.map((e, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: `${C.burgundy}08`, border: `1px solid ${C.burgundy}30`, borderRadius: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 14 }}>📨</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 700, fontSize: 12, color: C.burgundy }}>Escalation from {e.from} ({e.fromRole}): </span>
                <span style={{ fontSize: 12, color: C.muted }}>{e.msg}</span>
              </div>
              <Bdg label={e.priority} color={C.burgundy} />
            </div>
          ))}
        </div>
      )}

      <AIInsightsPanel pid={pid} onLog={onLog} persona={persona} />

      {pid === "ceo"        && <CEODash        {...dashProps} />}
      {pid === "cfo"        && <CFODash        {...dashProps} />}
      {pid === "cro"        && <CRODash        {...dashProps} />}
      {pid === "banker"     && <BankerDash     {...dashProps} />}
      {pid === "lending"    && <LendingDash    {...dashProps} />}
      {pid === "mortgage"   && <MortgageDash   {...dashProps} />}
      {pid === "wealth"     && <WealthDash     {...dashProps} />}
      {pid === "branch"     && <BranchMgrDash  {...dashProps} />}
      {pid === "ops"        && <OpsDash        {...dashProps} />}
      {pid === "compliance" && <ComplianceDash {...dashProps} />}
      {pid === "credit"     && <CreditDash     {...dashProps} />}

      {ModalComponent && <ModalComponent onClose={closeModal} />}
    </div>
  );
}
